import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IoAddOutline, IoTrashOutline, IoCreateOutline,
  IoReorderThreeOutline, IoFlashOutline, IoCloseOutline,
  IoRefreshOutline, IoChevronDownOutline, IoChevronUpOutline,
  IoLocationOutline, IoEyeOutline, IoEyeOffOutline,
} from 'react-icons/io5';
import { GoogleMap, Polyline, OverlayView, DirectionsRenderer, useJsApiLoader } from '@react-google-maps/api';
import api, {
  routesAPI, subRoutesAPI, salesPointsAPI, depotsAPI,
  exitCorridorsAPI, routeEntryPointsAPI, usersAPI,
} from '../services/api';
import { decodeToken } from '../utils/auth';
import styles from './RouteNetworkPage.module.css';
import RoutesPanel from './RoutesPanel';
import SalespointPicker from './SalespointPicker';

const MAPS_API_KEY = process.env.REACT_APP_MAPS_API_KEY || '';
const MAP_CONTAINER_STYLE = { width: '100%', flex: 1, minHeight: 0 };
const DEFAULT_CENTER = { lat: 0.3476, lng: 32.5825 };
const MAIN_STROKE = 4;
const BRANCH_STROKE = Math.round(MAIN_STROKE * 0.7);
const MAX_CHUNK = 27;

function buildSubroutePath(sr) {
  const pts = [];
  if (sr.fork_point_coords?.lat) pts.push({ lat: Number(sr.fork_point_coords.lat), lng: Number(sr.fork_point_coords.lng) });
  (sr.waypoints || [])
    .filter(w => w.salespoint?.lat && w.salespoint?.lng)
    .sort((a, b) => a.sequence_order - b.sequence_order)
    .forEach(w => pts.push({ lat: Number(w.salespoint.lat), lng: Number(w.salespoint.lng) }));
  if (sr.exit_point_coords?.lat) pts.push({ lat: Number(sr.exit_point_coords.lat), lng: Number(sr.exit_point_coords.lng) });
  return pts;
}

function buildCorridorPath(c, depotLocation) {
  const pts = [];
  // Always start from the depot so the road line begins at the gate
  if (depotLocation?.lat && depotLocation?.lng)
    pts.push({ lat: Number(depotLocation.lat), lng: Number(depotLocation.lng) });
  (c.waypoints || [])
    .filter(w => w.salespoint?.lat && w.salespoint?.lng)
    .sort((a, b) => a.sequence_order - b.sequence_order)
    .forEach(w => pts.push({ lat: Number(w.salespoint.lat), lng: Number(w.salespoint.lng) }));
  if (c.divergence_point_coords?.lat)
    pts.push({ lat: Number(c.divergence_point_coords.lat), lng: Number(c.divergence_point_coords.lng) });
  return pts;
}

async function calculateDirectionsForPoints(points) {
  if (!points || points.length < 2 || !window.google?.maps) return [];
  const service = new window.google.maps.DirectionsService();
  const chunks = [];
  if (points.length <= MAX_CHUNK) {
    chunks.push(points);
  } else {
    for (let i = 0; i < points.length - 1; i += MAX_CHUNK - 1) {
      const end = Math.min(i + MAX_CHUNK, points.length);
      chunks.push(points.slice(i, end));
      if (end >= points.length) break;
    }
  }
  const results = [];
  for (let i = 0; i < chunks.length; i++) {
    if (i > 0) await new Promise(r => setTimeout(r, 300));
    const chunk = chunks[i];
    const result = await new Promise(resolve => {
      service.route(
        {
          origin: chunk[0],
          destination: chunk[chunk.length - 1],
          waypoints: chunk.slice(1, -1).map(pt => ({ location: pt, stopover: true })),
          travelMode: window.google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false,
        },
        (res, status) => resolve(status === window.google.maps.DirectionsStatus.OK ? res : null),
      );
    });
    if (result) results.push(result);
  }
  return results;
}

const BRANCH_TYPES = [
  { value: 'spur', label: 'Spur (dead-end)' },
  { value: 'mainline_rejoin', label: 'Mainline Rejoin' },
  { value: 'branch_connector', label: 'Branch Connector' },
];

const ENTRY_TYPES = [
  { value: 'primary', label: 'Primary (from depot exit)' },
  { value: 'from_route', label: 'From Intersecting Route' },
  { value: 'from_branch', label: 'From Branch Connection' },
];

const DEFAULT_BRANCH_FORM = {
  code: '', name: '', branch_type: 'spur',
  parent_branch: '', exit_to_subroute: '',
  fork_point_lat: '', fork_point_lng: '',
  exit_point_lat: '', exit_point_lng: '',
  color_hex: '#F59E0B', corridor_meters: 100,
  independently_serviceable: true,
};

const DEFAULT_CORRIDOR_FORM = {
  name: '', depot_id: '', corridor_meters: 150, is_active: true,
  divergence_point_lat: '', divergence_point_lng: '',
  route_ids: [],
};

const DEFAULT_EP_FORM = {
  name: '', entry_type: 'primary',
  location_lat: '', location_lng: '',
  entry_radius_meters: 200,
  from_exit_corridor: '', from_route: '', from_subroute: '',
  nearest_waypoint_id: '',
  default_direction: 'forward',
  is_active: true,
};

const SECTIONS = [
  { key: 'routes', label: 'Routes' },
  { key: 'branches', label: 'Branches' },
  { key: 'exit-corridors', label: 'Exit Corridors' },
  { key: 'entry-points', label: 'Entry Points' },
];

// ── Shared helpers ────────────────────────────────────────────────────────────

function ErrorBar({ message, onClose }) {
  if (!message) return null;
  return (
    <div className={styles.errorBar}>
      {message}
      <button onClick={onClose}>×</button>
    </div>
  );
}

const ENTITY_FIELD_CANDIDATES = [
  'entityinstance', 'entity_instance', 'entityInstance',
  'reference_entity',
  'organization', 'organization_id',
  'customer', 'customer_id',
  'entity', 'entity_id',
  'depot', 'depot_id',
  'member_of', 'memberships',
];

function collectIdsFromValue(value, ids) {
  if (value === null || value === undefined || value === '') return;

  if (Array.isArray(value)) {
    value.forEach(item => collectIdsFromValue(item, ids));
    return;
  }

  if (typeof value === 'object') {
    if (value.id !== undefined && value.id !== null && value.id !== '') ids.add(String(value.id));
    if (value.uuid !== undefined && value.uuid !== null && value.uuid !== '') ids.add(String(value.uuid));
    ENTITY_FIELD_CANDIDATES.forEach(field => {
      if (value[field] !== undefined && value[field] !== value) {
        collectIdsFromValue(value[field], ids);
      }
    });
    return;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    ids.add(String(value));
  }
}

function collectIdsFromObjectFields(source, fieldNames) {
  const ids = new Set();
  if (!source || typeof source !== 'object') return ids;

  fieldNames.forEach(field => {
    if (source[field] !== undefined) collectIdsFromValue(source[field], ids);
  });

  return ids;
}

function getLoggedInUserEntityIds(...sources) {
  const ids = new Set();

  sources.forEach(source => {
    if (!source || typeof source !== 'object') return;

    collectIdsFromObjectFields(source, ENTITY_FIELD_CANDIDATES).forEach(id => ids.add(id));

    ['profile', 'user_profile', 'userProfile', 'user'].forEach(nestedKey => {
      const nested = source[nestedKey];
      if (!nested || nested === source) return;
      collectIdsFromObjectFields(nested, ENTITY_FIELD_CANDIDATES).forEach(id => ids.add(id));
    });
  });

  return ids;
}

function isMappedSalesPoint(sp) {
  return sp?.is_mapped === true
    || sp?.is_mapped === 'True'
    || sp?.is_mapped === 'true'
    || sp?.is_mapped === 1
    || sp?.is_mapped === '1';
}

function salesPointBelongsToUserEntity(sp, entityIds) {
  if (!sp || entityIds.size === 0) return false;

  const salesPointEntityIds = collectIdsFromObjectFields(sp, [
    'reference_entity',
    'entityinstance', 'entity_instance', 'entityInstance',
    'organization', 'organization_id',
    'customer', 'customer_id',
    'entity', 'entity_id',
    'depot', 'depot_id',
  ]);

  for (const id of salesPointEntityIds) {
    if (entityIds.has(id)) return true;
  }

  return false;
}

async function loadAllSalesPoints(preferredEntityId = null) {
  const paramCandidates = preferredEntityId
    ? [
        { customer_id: preferredEntityId, is_mapped: true },
        { customer: preferredEntityId, is_mapped: true },
        { entity_id: preferredEntityId, is_mapped: true },
        { entity_instance: preferredEntityId, is_mapped: true },
        { is_mapped: true },
      ]
    : [{ is_mapped: true }];

  let firstRes = null;
  for (const params of paramCandidates) {
    try {
      firstRes = await salesPointsAPI.getAll(params);
      break;
    } catch (err) {
      if (err?.response?.status !== 500) throw err;
    }
  }

  if (!firstRes) {
    throw new Error('Unable to load sales points');
  }

  let results = [];
  let nextUrl = null;

  if (Array.isArray(firstRes.data)) {
    results = firstRes.data;
  } else if (firstRes.data?.results) {
    results = firstRes.data.results;
    nextUrl = firstRes.data.next;
  } else if (firstRes.data?.features) {
    results = firstRes.data.features.map(feature => ({
      ...feature.properties,
      lat: feature.geometry?.coordinates?.[1],
      lng: feature.geometry?.coordinates?.[0],
    }));
  }

  while (nextUrl) {
    const pageRes = await api.get(nextUrl);
    if (!pageRes.data?.results) break;
    results = [...results, ...pageRes.data.results];
    nextUrl = pageRes.data.next;
  }

  return results;
}

// Layer that renders all salespoints as small dots with optional labels
function SalespointsLayer({ salesPoints, showLabels, onSelect }) {
  return (
    <>
      {salesPoints.filter(sp => sp.lat && sp.lng).map(sp => (
        <OverlayView
          key={sp.id}
          position={{ lat: Number(sp.lat), lng: Number(sp.lng) }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div className={styles.spDotWrap} onClick={() => onSelect(sp)}>
            <div className={styles.spDot} title={sp.name} />
            {showLabels && <span className={styles.spLabel}>{sp.name}</span>}
          </div>
        </OverlayView>
      ))}
    </>
  );
}

// Info popup shown when a salespoint dot is clicked on any map
function SalespointInfoPopup({ sp, onClose, routes = [], subroutes = [], corridors = [], currentRouteId = null, onAddDone }) {
  const [mode, setMode] = useState(null); // 'route' | 'branch' | 'corridor'
  const [pickId, setPickId] = useState('');
  const [saving, setSaving] = useState(false);
  const agents = sp.assigned_salesperson || [];

  const handleAdd = async () => {
    if (!pickId) return;
    setSaving(true);
    try {
      if (mode === 'route') {
        await routesAPI.addWaypoint(pickId, { salespoint_id: sp.id });
      } else if (mode === 'branch') {
        await subRoutesAPI.addWaypoint(pickId, { salespoint_id: sp.id });
      } else if (mode === 'corridor') {
        await exitCorridorsAPI.addWaypoint(pickId, { salespoint_id: sp.id });
      }
      onAddDone?.();
      setMode(null);
      setPickId('');
    } catch (err) {
      console.error('Add failed', err);
    } finally { setSaving(false); }
  };

  const pickOptions = mode === 'route' ? routes : mode === 'branch' ? subroutes : corridors;

  return (
    <div className={styles.spPopup}>
      <div className={styles.spPopupHeader}>
        <span className={styles.spPopupName}>{sp.name}</span>
        <button className={styles.closeBtn} onClick={onClose}><IoCloseOutline size={15} /></button>
      </div>
      <div className={styles.spPopupBody}>
        {sp.status && (
          <span className={`${styles.badge} ${sp.status === 'Confirmed' ? styles.badgeGreen : styles.badgeGray}`}>
            {sp.status}
          </span>
        )}
        {sp.phone_num && <div className={styles.spInfoRow}>{sp.phone_num}</div>}
        {agents.length > 0 && (
          <div className={styles.spInfoRow}>
            <strong>Agent: </strong>{agents.map(a => a.name).join(', ')}
          </div>
        )}
        {sp.description && (
          <div className={styles.spInfoRow} style={{ color: '#999', fontSize: 11 }}>
            {sp.description.slice(0, 80)}{sp.description.length > 80 ? '…' : ''}
          </div>
        )}
      </div>

      {mode ? (
        <div className={styles.spPopupActions}>
          <select className={styles.input} value={pickId} onChange={e => setPickId(e.target.value)}>
            <option value="">— Select {mode} —</option>
            {pickOptions.map(item => (
              <option key={item.id} value={item.id}>
                {item.code || item.name}{item.code && item.name ? ` — ${item.name}` : ''}
              </option>
            ))}
          </select>
          <div className={styles.spPopupBtns}>
            <button
              className={styles.saveBtn}
              style={{ padding: '5px 10px', fontSize: 12 }}
              disabled={!pickId || saving}
              onClick={handleAdd}
            >
              {saving ? '…' : 'Add'}
            </button>
            <button className={styles.cancelBtnSm} onClick={() => { setMode(null); setPickId(''); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.spPopupActions}>
          {routes.length > 0 && (
            <button className={styles.spPopupBtn} onClick={() => { setMode('route'); setPickId(currentRouteId || ''); }}>
              <IoAddOutline size={12} /> Add to Route
            </button>
          )}
          {subroutes.length > 0 && (
            <button className={styles.spPopupBtn} onClick={() => { setMode('branch'); setPickId(''); }}>
              <IoAddOutline size={12} /> Add to Branch
            </button>
          )}
          {corridors.length > 0 && (
            <button className={styles.spPopupBtn} onClick={() => { setMode('corridor'); setPickId(''); }}>
              <IoAddOutline size={12} /> Add to Corridor
            </button>
          )}
          {routes.length === 0 && subroutes.length === 0 && corridors.length === 0 && (
            <span style={{ fontSize: 11, color: '#aaa' }}>Select a route to add this stop</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── RoutesSection ─────────────────────────────────────────────────────────────

function RoutesSection({ salesPoints, routes }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_API_KEY });

  const [selectedRouteIds, setSelectedRouteIds] = useState([]);
  const [routeDataById, setRouteDataById] = useState({});
  const [mainDirectionsById, setMainDirectionsById] = useState({});
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(12);
  const [loadingRouteIds, setLoadingRouteIds] = useState(new Set());
  const [showSubroutes, setShowSubroutes] = useState(true);
  const [showCorridors, setShowCorridors] = useState(true);
  const [showSalespoints, setShowSalespoints] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [showMapLabels, setShowMapLabels] = useState(true);
  const [selectedSP, setSelectedSP] = useState(null);

  const [subrouteDirections, setSubrouteDirections] = useState({});
  const [corridorDirections, setCorridorDirections] = useState({});

  const [hiddenBranchIds, setHiddenBranchIds] = useState(new Set());
  const [branchColors, setBranchColors] = useState({});
  const colorSaveTimer = useRef({});

  const toggleBranchVisibility = (id) => {
    setHiddenBranchIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBranchColorChange = (id, color) => {
    setBranchColors(prev => ({ ...prev, [id]: color }));
    clearTimeout(colorSaveTimer.current[id]);
    colorSaveTimer.current[id] = setTimeout(() => {
      subRoutesAPI.patch(id, { color_hex: color }).catch(err => console.error('color save failed', err));
    }, 600);
  };

  const loadRouteData = useCallback(async (routeId, { recenter } = {}) => {
    setLoadingRouteIds(prev => { const n = new Set(prev); n.add(routeId); return n; });
    try {
      const [detailRes, srRes, corrRes] = await Promise.all([
        routesAPI.get(routeId),
        subRoutesAPI.list(routeId),
        exitCorridorsAPI.list(null),
      ]);
      const detail = detailRes.data;
      const subroutes = srRes.data?.results || srRes.data || [];
      const allCorridors = corrRes.data?.results || corrRes.data || [];
      const corridors = allCorridors.filter(c =>
        (c.route_ids || []).some(rid => String(rid) === String(routeId))
      );
      const path = detail.path_coordinates || [];
      if (recenter && path.length > 0) {
        const mid = path[Math.floor(path.length / 2)];
        setMapCenter({ lat: Number(mid.lat), lng: Number(mid.lng) });
        setMapZoom(13);
      }
      setRouteDataById(prev => ({ ...prev, [routeId]: { route: detail, path, subroutes, corridors } }));

      setBranchColors(prev => {
        const next = { ...prev };
        subroutes.forEach(sr => { if (next[sr.id] == null) next[sr.id] = sr.color_hex || '#6B21A8'; });
        return next;
      });

      const mainPoints = (detail.waypoints || [])
        .filter(wp => wp.salespoint?.lat && wp.salespoint?.lng)
        .sort((a, b) => a.sequence_order - b.sequence_order)
        .map(wp => ({ lat: Number(wp.salespoint.lat), lng: Number(wp.salespoint.lng) }));
      calculateDirectionsForPoints(mainPoints).then(dirs => {
        if (dirs.length > 0) setMainDirectionsById(prev => ({ ...prev, [routeId]: dirs }));
      });

      subroutes.forEach(sr => {
        const pts = buildSubroutePath(sr);
        if (pts.length < 2) return;
        calculateDirectionsForPoints(pts).then(dirs => {
          if (dirs.length > 0) setSubrouteDirections(prev => ({ ...prev, [sr.id]: dirs }));
        });
      });

      corridors.forEach(c => {
        const pts = buildCorridorPath(c, detail.depot_location);
        if (pts.length < 2) return;
        calculateDirectionsForPoints(pts).then(dirs => {
          if (dirs.length > 0) setCorridorDirections(prev => ({ ...prev, [c.id]: dirs }));
        });
      });
    } catch (err) {
      console.error('Map load error:', err);
    } finally {
      setLoadingRouteIds(prev => { const n = new Set(prev); n.delete(routeId); return n; });
    }
  }, []);

  const handleRouteSelect = useCallback(async (route) => {
    if (!route?.id) return;
    if (selectedRouteIds.includes(route.id)) {
      // Deselect: drop the route and any data tied only to it
      setSelectedRouteIds(prev => prev.filter(id => id !== route.id));
      setRouteDataById(prev => {
        const removed = prev[route.id];
        const next = { ...prev };
        delete next[route.id];
        if (removed) {
          // Strip subroute / corridor directions that belong only to this route
          const subIds = new Set((removed.subroutes || []).map(s => s.id));
          const corrIds = new Set((removed.corridors || []).map(c => c.id));
          if (subIds.size > 0) {
            setSubrouteDirections(p => {
              const n = { ...p };
              subIds.forEach(id => delete n[id]);
              return n;
            });
            setHiddenBranchIds(p => {
              const n = new Set(p);
              subIds.forEach(id => n.delete(id));
              return n;
            });
          }
          if (corrIds.size > 0) {
            setCorridorDirections(p => {
              const n = { ...p };
              corrIds.forEach(id => delete n[id]);
              return n;
            });
          }
        }
        return next;
      });
      setMainDirectionsById(prev => { const n = { ...prev }; delete n[route.id]; return n; });
      setSelectedSP(null);
      return;
    }
    // Select
    const isFirst = selectedRouteIds.length === 0;
    setSelectedRouteIds(prev => [...prev, route.id]);
    setSelectedSP(null);
    loadRouteData(route.id, { recenter: isFirst });
  }, [selectedRouteIds, loadRouteData]);

  const selectedRoutesData = selectedRouteIds
    .map(id => routeDataById[id])
    .filter(Boolean);

  const isLoadingAny = loadingRouteIds.size > 0;
  const primaryRouteId = selectedRouteIds[0] || null;

  return (
    <div className={styles.routesSplit}>
      <div className={styles.routesList}>
        <RoutesPanel
          onRouteSelect={handleRouteSelect}
          selectedRouteIds={selectedRouteIds}
        />
      </div>

      <div className={styles.routesMapPanel}>
        {selectedRouteIds.length === 0 ? (
          <div className={styles.mapPlaceholder}>
            <IoLocationOutline size={44} />
            <p>Select one or more routes to visualise on the map</p>
          </div>
        ) : !isLoaded ? (
          <div className={styles.mapPlaceholder}><p>Loading map…</p></div>
        ) : (
          <>
            <div className={styles.mapToggleBar}>
              <span className={styles.mapRouteLabel}>
                {selectedRoutesData.length === 1 ? (
                  <>
                    <span
                      className={styles.mapRouteDot}
                      style={{ background: selectedRoutesData[0].route.color_hex || '#2196F3' }}
                    />
                    {selectedRoutesData[0].route.name}
                  </>
                ) : (
                  <>{selectedRouteIds.length} routes selected</>
                )}
              </span>
              <div className={styles.mapToggles}>
                <button className={`${styles.mapToggleBtn} ${showSubroutes ? styles.mapToggleActive : ''}`} onClick={() => setShowSubroutes(v => !v)}>
                  <span className={styles.toggleSwatch} style={{ background: '#6B21A8' }} />Branches
                </button>
                <button className={`${styles.mapToggleBtn} ${showCorridors ? styles.mapToggleActive : ''}`} onClick={() => setShowCorridors(v => !v)}>
                  <span className={styles.toggleSwatch} style={{ background: '#EF4444' }} />Corridors
                </button>
                <button className={`${styles.mapToggleBtn} ${showSalespoints ? styles.mapToggleActive : ''}`} onClick={() => setShowSalespoints(v => !v)}>
                  Salespoints
                </button>
                <button className={`${styles.mapToggleBtn} ${showLabels ? styles.mapToggleActive : ''}`} onClick={() => setShowLabels(v => !v)}>
                  Labels
                </button>
                <button className={`${styles.mapToggleBtn} ${showMapLabels ? styles.mapToggleActive : ''}`} onClick={() => setShowMapLabels(v => !v)} title="Toggle Google map place labels">
                  Map Labels
                </button>
                {isLoadingAny && <span className={styles.mapLoadingBadge}>Loading…</span>}
              </div>
            </div>

            <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              center={mapCenter}
              zoom={mapZoom}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                zoomControlOptions: { position: 7 },
                styles: showMapLabels ? undefined : [{ elementType: 'labels', stylers: [{ visibility: 'off' }] }],
              }}
            >
              {selectedRoutesData.map(data => {
                const r = data.route;
                const routeColor = r.color_hex || '#2196F3';
                const dirs = mainDirectionsById[r.id] || [];
                return (
                  <React.Fragment key={`route-${r.id}`}>
                    {/* Main route line */}
                    {dirs.length > 0
                      ? dirs.map((dir, i) => (
                          <DirectionsRenderer key={`main-${r.id}-${i}`} directions={dir}
                            options={{ suppressMarkers: true, polylineOptions: { strokeColor: routeColor, strokeWeight: MAIN_STROKE, strokeOpacity: 0.9, zIndex: 1 } }} />
                        ))
                      : data.path?.length > 1 && (
                          <Polyline key={`mainpoly-${r.id}`} path={data.path}
                            options={{ strokeColor: routeColor, strokeWeight: MAIN_STROKE, strokeOpacity: 0.6, zIndex: 1 }} />
                        )
                    }

                    {/* Waypoint dots */}
                    {(r.waypoints || []).map(wp => {
                      if (!wp.salespoint?.lat || !wp.salespoint?.lng) return null;
                      return (
                        <OverlayView key={`wp-${r.id}-${wp.id}`}
                          position={{ lat: Number(wp.salespoint.lat), lng: Number(wp.salespoint.lng) }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                          <div className={styles.wpMarkerWrap}>
                            <div className={styles.waypointDot} style={{ background: routeColor }}
                              onClick={() => { const full = salesPoints.find(s => s.id === wp.salespoint.id); if (full) setSelectedSP(full); }} />
                            {showLabels && <span className={styles.wpLabel}>{wp.sequence_order}. {wp.salespoint.name}</span>}
                          </div>
                        </OverlayView>
                      );
                    })}

                    {/* Depot marker */}
                    {r.depot_location?.lat && (
                      <OverlayView key={`depot-${r.id}`}
                        position={{ lat: Number(r.depot_location.lat), lng: Number(r.depot_location.lng) }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div className={styles.depotMarkerWrap}>
                          <div className={styles.depotMarker}>D</div>
                          {showLabels && <span className={styles.depotLabel}>{r.depot?.name || 'Depot'}</span>}
                        </div>
                      </OverlayView>
                    )}

                    {/* Corridor waypoint dots */}
                    {showCorridors && (data.corridors || []).flatMap(c =>
                      (c.waypoints || [])
                        .filter(w => w.salespoint?.lat && w.salespoint?.lng)
                        .map(w => (
                          <OverlayView key={`cwp-${r.id}-${w.id}`}
                            position={{ lat: Number(w.salespoint.lat), lng: Number(w.salespoint.lng) }}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                            <div className={styles.corridorWpMarkerWrap}>
                              <div className={styles.corridorWpDot}
                                onClick={() => { const full = salesPoints.find(s => s.id === w.salespoint.id); if (full) setSelectedSP(full); }}
                                title={w.salespoint.name} />
                              {showLabels && <span className={styles.wpLabel}>{w.sequence_order}. {w.salespoint.name}</span>}
                            </div>
                          </OverlayView>
                        ))
                    )}

                    {/* Divergence points */}
                    {showCorridors && (data.corridors || []).filter(c => c.divergence_point_coords?.lat).map(c => (
                      <OverlayView key={`div-${r.id}-${c.id}`}
                        position={{ lat: Number(c.divergence_point_coords.lat), lng: Number(c.divergence_point_coords.lng) }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                        <div className={styles.divergenceMarkerWrap}>
                          <div className={styles.divergenceMarker} title={`${c.name} — divergence point`} />
                          {showLabels && <span className={styles.divergenceLabel}>{c.name} split</span>}
                        </div>
                      </OverlayView>
                    ))}

                    {/* Subroutes */}
                    {showSubroutes && (data.subroutes || []).map(sr => {
                      if (hiddenBranchIds.has(sr.id)) return null;
                      const color = branchColors[sr.id] || sr.color_hex || '#6B21A8';
                      const sdirs = subrouteDirections[sr.id];
                      if (sdirs?.length > 0) {
                        return sdirs.map((d, i) => (
                          <DirectionsRenderer key={`sr-${sr.id}-${i}`} directions={d}
                            options={{ suppressMarkers: true, polylineOptions: { strokeColor: color, strokeWeight: BRANCH_STROKE, strokeOpacity: 0.9, zIndex: 2 } }} />
                        ));
                      }
                      return null;
                    })}

                    {/* Exit corridors */}
                    {showCorridors && (data.corridors || []).map(c => {
                      const cdirs = corridorDirections[c.id];
                      if (cdirs?.length > 0) {
                        return cdirs.map((d, i) => (
                          <DirectionsRenderer key={`corr-${c.id}-${i}`} directions={d}
                            options={{ suppressMarkers: true, polylineOptions: { strokeColor: '#EF4444', strokeWeight: MAIN_STROKE, strokeOpacity: 0.85, zIndex: 2 } }} />
                        ));
                      }
                      return null;
                    })}
                  </React.Fragment>
                );
              })}

              {/* Salespoints layer */}
              {showSalespoints && (
                <SalespointsLayer salesPoints={salesPoints} showLabels={showLabels} onSelect={setSelectedSP} />
              )}
            </GoogleMap>

            {/* Legend */}
            {selectedRoutesData.length > 0 && (
              <div className={styles.mapLegend}>
                {selectedRoutesData.map((data, idx) => {
                  const r = data.route;
                  const routeColor = r.color_hex || '#2196F3';
                  return (
                    <React.Fragment key={`legend-${r.id}`}>
                      {idx > 0 && <div className={styles.legendDivider} />}
                      <div className={styles.legendRow}>
                        <span className={styles.legendLine} style={{ background: routeColor }} />
                        <span className={styles.legendLabel}>{r.code} — {r.name}</span>
                      </div>
                      {(data.subroutes || []).length > 0 && (
                        <>
                          <div className={styles.legendGroupTitle}>Branches</div>
                          {data.subroutes.map(sr => {
                            const color = branchColors[sr.id] || sr.color_hex || '#6B21A8';
                            const hidden = hiddenBranchIds.has(sr.id);
                            return (
                              <div key={sr.id} className={`${styles.legendRow} ${hidden ? styles.legendRowHidden : ''}`}>
                                <label className={styles.colorSwatch} title="Change colour">
                                  <span style={{ background: color }} />
                                  <input type="color" value={color} onChange={e => handleBranchColorChange(sr.id, e.target.value)} className={styles.hiddenColorInput} />
                                </label>
                                <span className={styles.legendLabel}>{sr.code}{sr.name ? ` — ${sr.name}` : ''}</span>
                                <button className={styles.eyeBtn} onClick={() => toggleBranchVisibility(sr.id)}>
                                  {hidden ? <IoEyeOffOutline size={13} /> : <IoEyeOutline size={13} />}
                                </button>
                              </div>
                            );
                          })}
                        </>
                      )}
                      {(data.corridors || []).length > 0 && showCorridors && (
                        <>
                          <div className={styles.legendGroupTitle}>Exit Corridors</div>
                          {data.corridors.map(c => (
                            <div key={c.id} className={styles.legendRow}>
                              <span className={styles.legendLine} style={{ background: '#EF4444' }} />
                              <span className={styles.legendLabel}>{c.name}</span>
                            </div>
                          ))}
                        </>
                      )}
                      {r.depot_location?.lat && (
                        <div className={styles.legendRow}>
                          <span className={styles.legendDepotSwatch} />
                          <span className={styles.legendLabel}>{r.depot?.name || 'Depot'}</span>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {/* Salespoint info popup */}
            {selectedSP && (
              <SalespointInfoPopup
                sp={selectedSP}
                onClose={() => setSelectedSP(null)}
                routes={routes}
                subroutes={selectedRoutesData.flatMap(d => d.subroutes || [])}
                corridors={selectedRoutesData.flatMap(d => d.corridors || [])}
                currentRouteId={primaryRouteId}
                onAddDone={() => {
                  selectedRouteIds.forEach(id => loadRouteData(id));
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── BranchesSection ───────────────────────────────────────────────────────────

function BranchesSection({ routes, salesPoints }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_API_KEY });

  // List state
  const [subroutes, setSubroutes] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_BRANCH_FORM });
  const [formRouteId, setFormRouteId] = useState('');
  const [showWaypointPicker, setShowWaypointPicker] = useState(null);
  const [reordered, setReordered] = useState({});
  const [pendingReorder, setPendingReorder] = useState({});
  const [dragged, setDragged] = useState(null);
  // Route stops management
  const [routeStopsExpanded, setRouteStopsExpanded] = useState(false);
  const [showRouteWaypointPicker, setShowRouteWaypointPicker] = useState(false);
  const [reorderedRoute, setReorderedRoute] = useState(null);
  const [pendingRouteReorder, setPendingRouteReorder] = useState(false);
  const [draggedRoute, setDraggedRoute] = useState(null);

  // Map pick state
  const [pickingForkPoint, setPickingForkPoint] = useState(false);
  const [pickingExitPoint, setPickingExitPoint] = useState(false);

  // Map state
  const [routeDetail, setRouteDetail] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(12);
  const [loadingMap, setLoadingMap] = useState(false);
  const [showSalespoints, setShowSalespoints] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [selectedSP, setSelectedSP] = useState(null);
  const [mainDirections, setMainDirections] = useState([]);
  const [subrouteDirections, setSubrouteDirections] = useState({});
  const [hiddenBranchIds, setHiddenBranchIds] = useState(new Set());
  const [branchColors, setBranchColors] = useState({});
  const colorSaveTimer = useRef({});

  const toggleBranchVisibility = (id) => {
    setHiddenBranchIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBranchColorChange = (id, color) => {
    setBranchColors(prev => ({ ...prev, [id]: color }));
    clearTimeout(colorSaveTimer.current[id]);
    colorSaveTimer.current[id] = setTimeout(() => {
      subRoutesAPI.patch(id, { color_hex: color }).catch(() => {});
    }, 600);
  };

  const loadBranchesData = useCallback(async (routeId) => {
    setLoading(true);
    setLoadingMap(true);
    setMainDirections([]);
    setSubrouteDirections({});
    setRouteDetail(null);
    setBranchColors({});
    setHiddenBranchIds(new Set());
    setSelectedSP(null);
    try {
      const [srRes, detailRes] = await Promise.all([
        subRoutesAPI.list(routeId),
        routesAPI.get(routeId),
      ]);
      const subs = srRes.data?.results || srRes.data || [];
      const detail = detailRes.data;
      setSubroutes(subs);
      setRouteDetail(detail);

      const path = detail.path_coordinates || [];
      if (path.length > 0) {
        const mid = path[Math.floor(path.length / 2)];
        setMapCenter({ lat: Number(mid.lat), lng: Number(mid.lng) });
        setMapZoom(13);
      }
      const colors = {};
      subs.forEach(sr => { colors[sr.id] = sr.color_hex || '#6B21A8'; });
      setBranchColors(colors);
      setLoadingMap(false);

      const mainPts = (detail.waypoints || [])
        .filter(wp => wp.salespoint?.lat && wp.salespoint?.lng)
        .sort((a, b) => a.sequence_order - b.sequence_order)
        .map(wp => ({ lat: Number(wp.salespoint.lat), lng: Number(wp.salespoint.lng) }));
      calculateDirectionsForPoints(mainPts).then(dirs => {
        if (dirs.length > 0) setMainDirections(dirs);
      });
      subs.forEach(sr => {
        const pts = buildSubroutePath(sr);
        if (pts.length < 2) return;
        calculateDirectionsForPoints(pts).then(dirs => {
          if (dirs.length > 0) setSubrouteDirections(prev => ({ ...prev, [sr.id]: dirs }));
        });
      });
    } catch { setError('Failed to load branches'); setLoadingMap(false); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    setRouteStopsExpanded(false);
    setShowRouteWaypointPicker(false);
    setReorderedRoute(null);
    setPendingRouteReorder(false);
    if (selectedRouteId) loadBranchesData(selectedRouteId);
    else { setSubroutes([]); setRouteDetail(null); setMainDirections([]); setSubrouteDirections({}); }
  }, [selectedRouteId, loadBranchesData]);

  useEffect(() => {
    const picking = pickingForkPoint || pickingExitPoint;
    if (!picking) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setPickingForkPoint(false);
        setPickingExitPoint(false);
        setShowForm(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickingForkPoint, pickingExitPoint]);

  const openCreateForm = () => {
    setFormData({ ...DEFAULT_BRANCH_FORM });
    setFormRouteId(selectedRouteId || '');
    setEditingBranch(null);
    setShowForm(true);
  };

  const openEditForm = (branch) => {
    setFormData({
      code: branch.code || '',
      name: branch.name || '',
      branch_type: branch.branch_type || 'spur',
      parent_branch: branch.parent_branch || '',
      exit_to_subroute: branch.exit_to_subroute || '',
      fork_point_lat: branch.fork_point_coords?.lat ?? '',
      fork_point_lng: branch.fork_point_coords?.lng ?? '',
      exit_point_lat: branch.exit_point_coords?.lat ?? '',
      exit_point_lng: branch.exit_point_coords?.lng ?? '',
      color_hex: branch.color_hex || '#F59E0B',
      corridor_meters: branch.corridor_meters || 100,
      independently_serviceable: branch.independently_serviceable !== false,
    });
    setFormRouteId(String(branch.parent_route));
    setEditingBranch(branch);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const routeIdToUse = editingBranch ? String(editingBranch.parent_route) : formRouteId;
    if (!routeIdToUse) { setError('Please select a parent route'); return; }
    const payload = {
      ...formData,
      parent_branch: formData.parent_branch || null,
      exit_to_subroute: formData.exit_to_subroute || null,
      fork_point_lat: formData.fork_point_lat !== '' ? parseFloat(formData.fork_point_lat) : null,
      fork_point_lng: formData.fork_point_lng !== '' ? parseFloat(formData.fork_point_lng) : null,
      exit_point_lat: formData.exit_point_lat !== '' ? parseFloat(formData.exit_point_lat) : null,
      exit_point_lng: formData.exit_point_lng !== '' ? parseFloat(formData.exit_point_lng) : null,
    };
    try {
      setLoading(true);
      if (editingBranch) {
        await subRoutesAPI.patch(editingBranch.id, payload);
      } else {
        await subRoutesAPI.create({ parent_route_id: routeIdToUse, ...payload });
      }
      setShowForm(false);
      const routeToRefresh = selectedRouteId || routeIdToUse;
      if (routeToRefresh) loadBranchesData(routeToRefresh);
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save branch');
    } finally { setLoading(false); }
  };

  const handleDelete = async (branch) => {
    if (!window.confirm(`Delete branch "${branch.code}"?`)) return;
    try {
      setLoading(true);
      await subRoutesAPI.delete(branch.id);
      if (selectedRouteId) loadBranchesData(selectedRouteId);
    } catch { setError('Failed to delete branch'); }
    finally { setLoading(false); }
  };

  const handleAddWaypoints = async (subrouteId, orderedIds) => {
    try {
      setLoading(true);
      await subRoutesAPI.addWaypointsBulk(subrouteId, { salespoint_ids: orderedIds });
      setShowWaypointPicker(null);
      if (selectedRouteId) loadBranchesData(selectedRouteId);
    } catch { setError('Failed to add stops'); }
    finally { setLoading(false); }
  };

  const handleRemoveWaypoint = async (subrouteId, waypointId) => {
    try {
      setLoading(true);
      await subRoutesAPI.removeWaypoint(subrouteId, waypointId);
      if (selectedRouteId) loadBranchesData(selectedRouteId);
    } catch { setError('Failed to remove stop'); }
    finally { setLoading(false); }
  };

  const handleOptimize = async (subrouteId) => {
    try {
      setLoading(true);
      const res = await subRoutesAPI.optimize(subrouteId);
      setSubroutes(prev => prev.map(s => s.id === subrouteId ? res.data : s));
    } catch { setError('Failed to optimize'); }
    finally { setLoading(false); }
  };

  // Route stop handlers
  const handleAddRouteWaypoints = async (orderedIds) => {
    try {
      setLoading(true);
      await routesAPI.addWaypointsBulk(selectedRouteId, { salespoint_ids: orderedIds });
      setShowRouteWaypointPicker(false);
      loadBranchesData(selectedRouteId);
    } catch { setError('Failed to add stops to route'); }
    finally { setLoading(false); }
  };

  const handleRemoveRouteWaypoint = async (waypointId) => {
    try {
      setLoading(true);
      await routesAPI.removeWaypoint(selectedRouteId, waypointId);
      loadBranchesData(selectedRouteId);
    } catch { setError('Failed to remove route stop'); }
    finally { setLoading(false); }
  };

  const handleRouteDragStart = (e, wp) => {
    setDraggedRoute(wp);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleRouteDragOver = (e, targetWp) => {
    e.preventDefault();
    if (!draggedRoute || draggedRoute.id === targetWp.id) return;
    const current = reorderedRoute || [...(routeDetail?.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);
    const dIdx = current.findIndex(w => w.id === draggedRoute.id);
    const tIdx = current.findIndex(w => w.id === targetWp.id);
    if (dIdx === -1 || tIdx === -1) return;
    const next = [...current];
    const [removed] = next.splice(dIdx, 1);
    next.splice(tIdx, 0, removed);
    setReorderedRoute(next);
    setPendingRouteReorder(true);
  };

  const handleSaveRouteOrder = async () => {
    if (!reorderedRoute) return;
    try {
      setLoading(true);
      await routesAPI.reorderWaypoints(selectedRouteId, reorderedRoute.map(w => w.id));
      setPendingRouteReorder(false);
      setReorderedRoute(null);
      loadBranchesData(selectedRouteId);
    } catch { setError('Failed to save route order'); }
    finally { setLoading(false); }
  };

  const cancelRouteReorder = () => {
    setReorderedRoute(null);
    setPendingRouteReorder(false);
  };

  const getRouteWaypoints = () =>
    reorderedRoute || [...(routeDetail?.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);

  const handleDragStart = (e, wp, subrouteId) => {
    setDragged({ ...wp, subrouteId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetWp, subrouteId) => {
    e.preventDefault();
    if (!dragged || dragged.id === targetWp.id) return;
    const subroute = subroutes.find(s => s.id === subrouteId);
    if (!subroute) return;
    const current = reordered[subrouteId] || [...(subroute.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);
    const dIdx = current.findIndex(w => w.id === dragged.id);
    const tIdx = current.findIndex(w => w.id === targetWp.id);
    if (dIdx === -1 || tIdx === -1) return;
    const next = [...current];
    const [removed] = next.splice(dIdx, 1);
    next.splice(tIdx, 0, removed);
    setReordered(prev => ({ ...prev, [subrouteId]: next }));
    setPendingReorder(prev => ({ ...prev, [subrouteId]: true }));
  };

  const handleSaveOrder = async (subrouteId) => {
    const newOrder = reordered[subrouteId];
    if (!newOrder) return;
    try {
      setLoading(true);
      await subRoutesAPI.reorderWaypoints(subrouteId, newOrder.map(w => w.id));
      setPendingReorder(prev => ({ ...prev, [subrouteId]: false }));
      setReordered(prev => { const u = { ...prev }; delete u[subrouteId]; return u; });
      if (selectedRouteId) loadBranchesData(selectedRouteId);
    } catch { setError('Failed to save order'); }
    finally { setLoading(false); }
  };

  const cancelReorder = (subrouteId) => {
    setReordered(prev => { const u = { ...prev }; delete u[subrouteId]; return u; });
    setPendingReorder(prev => ({ ...prev, [subrouteId]: false }));
  };

  const getWaypoints = (subroute) =>
    reordered[subroute.id] || [...(subroute.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);

  const branchOptions = subroutes.filter(s => !editingBranch || s.id !== editingBranch.id);
  const routeColor = routeDetail?.color_hex || '#2196F3';

  const depotId = routeDetail?.depot?.id;
  const contextSalesPoints = depotId
    ? salesPoints.filter(sp => (sp.reference_entity || []).some(e => String(e.id) === String(depotId)))
    : salesPoints;

  return (
    <div className={styles.sectionSplit}>
      {/* ── Left: branch list ── */}
      <div className={styles.sectionList}>
        <ErrorBar message={error} onClose={() => setError(null)} />
        <div className={styles.sectionToolbar}>
          <select className={styles.filterSelect} value={selectedRouteId} onChange={e => setSelectedRouteId(e.target.value)}>
            <option value="">— Select a route —</option>
            {routes.map(r => <option key={r.id} value={r.id}>{r.code} — {r.name}</option>)}
          </select>
          <button className={styles.addBtn} onClick={openCreateForm}><IoAddOutline size={16} /> New Branch</button>
        </div>

        {loading && <div className={styles.loadingBar}>Loading...</div>}
        {selectedRouteId && !loading && subroutes.length === 0 && (
          <div className={styles.empty}>No branches on this route.</div>
        )}

        {/* ── Route Stops card ── */}
        {routeDetail && (
          <div className={styles.card} style={{ borderLeftColor: routeColor }}>
            <div className={styles.cardHeader}>
              <span className={styles.colorDot} style={{ background: routeColor }} />
              <span className={styles.cardCode}>{routeDetail.code}</span>
              <span className={styles.cardName}>{routeDetail.name}</span>
              <span className={styles.cardBadge} style={{ background: '#1C3520', color: '#F9F3E7' }}>main route</span>
              <span className={styles.cardMeta}>{(routeDetail.waypoints || []).length} stops</span>
              <div className={styles.cardActions}>
                <button className={styles.iconBtn} onClick={() => setRouteStopsExpanded(v => !v)}>
                  {routeStopsExpanded ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                </button>
              </div>
            </div>

            {routeStopsExpanded && (
              <div className={styles.cardBody}>
                <div className={styles.waypointList}>
                  {getRouteWaypoints().length === 0 && <div className={styles.emptySmall}>No stops yet.</div>}
                  {getRouteWaypoints().map((wp, idx) => (
                    <div key={wp.id} className={styles.waypointRow}
                      draggable
                      onDragStart={e => handleRouteDragStart(e, wp)}
                      onDragOver={e => handleRouteDragOver(e, wp)}
                      onDragEnd={() => setDraggedRoute(null)}>
                      <IoReorderThreeOutline size={16} className={styles.dragHandle} />
                      <span className={styles.wpNum}>{idx + 1}</span>
                      <span className={styles.wpName}>{wp.salespoint?.name || '—'}</span>
                      <button className={`${styles.iconBtn} ${styles.danger}`} onClick={() => handleRemoveRouteWaypoint(wp.id)}><IoTrashOutline size={14} /></button>
                    </div>
                  ))}
                </div>

                <div className={styles.cardFooter}>
                  {pendingRouteReorder ? (
                    <>
                      <button className={styles.saveBtn} onClick={handleSaveRouteOrder}>Save Order</button>
                      <button className={styles.cancelBtnSm} onClick={cancelRouteReorder}>Cancel</button>
                    </>
                  ) : (
                    <button className={styles.addStopsBtn} onClick={() => setShowRouteWaypointPicker(true)}>+ Add Stops to Route</button>
                  )}
                </div>

                {showRouteWaypointPicker && (
                  <SalespointPicker
                    salesPoints={contextSalesPoints}
                    excludeIds={new Set((routeDetail.waypoints || []).map(w => w.salespoint?.id).filter(Boolean))}
                    onSubmit={ids => handleAddRouteWaypoints(ids)}
                    onCancel={() => setShowRouteWaypointPicker(false)}
                    onRowClick={sp => {
                      setSelectedSP(sp);
                      const lat = parseFloat(sp.lat || sp.latitude);
                      const lng = parseFloat(sp.lng || sp.longitude);
                      if (!isNaN(lat) && !isNaN(lng)) { setMapCenter({ lat, lng }); setMapZoom(16); }
                    }}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {subroutes.map(branch => {
          const isExpanded = expandedId === branch.id;
          const waypoints = getWaypoints(branch);
          const hasPending = !!pendingReorder[branch.id];
          return (
            <div key={branch.id} className={styles.card} style={{ borderLeftColor: branch.color_hex || '#F59E0B' }}>
              <div className={styles.cardHeader}>
                <span className={styles.colorDot} style={{ background: branch.color_hex || '#F59E0B' }} />
                <span className={styles.cardCode}>{branch.code}</span>
                <span className={styles.cardName}>{branch.name}</span>
                <span className={styles.cardBadge}>{branch.branch_type}</span>
                <span className={styles.cardMeta}>{branch.waypoint_count || 0} stops</span>
                <div className={styles.cardActions}>
                  <button className={styles.iconBtn} onClick={() => setExpandedId(isExpanded ? null : branch.id)}>
                    {isExpanded ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                  </button>
                  <button className={styles.iconBtn} onClick={() => openEditForm(branch)}><IoCreateOutline size={16} /></button>
                  <button className={`${styles.iconBtn} ${styles.danger}`} onClick={() => handleDelete(branch)}><IoTrashOutline size={16} /></button>
                </div>
              </div>

              {isExpanded && (
                <div className={styles.cardBody}>
                  <div className={styles.metaGrid}>
                    {branch.fork_point_coords && (
                      <div className={styles.metaItem}><strong>Fork:</strong> {branch.fork_point_coords.lat.toFixed(6)}, {branch.fork_point_coords.lng.toFixed(6)}</div>
                    )}
                    {branch.exit_point_coords && (
                      <div className={styles.metaItem}><strong>Exit:</strong> {branch.exit_point_coords.lat.toFixed(6)}, {branch.exit_point_coords.lng.toFixed(6)}</div>
                    )}
                    {branch.parent_branch && (
                      <div className={styles.metaItem}><strong>Parent:</strong> {subroutes.find(s => s.id === branch.parent_branch)?.code || branch.parent_branch}</div>
                    )}
                  </div>

                  <div className={styles.waypointList}>
                    {waypoints.length === 0 && <div className={styles.emptySmall}>No stops yet.</div>}
                    {waypoints.map((wp, idx) => (
                      <div key={wp.id} className={styles.waypointRow}
                        draggable onDragStart={e => handleDragStart(e, wp, branch.id)}
                        onDragOver={e => handleDragOver(e, wp, branch.id)}
                        onDragEnd={() => setDragged(null)}>
                        <IoReorderThreeOutline size={16} className={styles.dragHandle} />
                        <span className={styles.wpNum}>{idx + 1}</span>
                        <span className={styles.wpName}>{wp.salespoint?.name || '—'}</span>
                        <button className={`${styles.iconBtn} ${styles.danger}`} onClick={() => handleRemoveWaypoint(branch.id, wp.id)}><IoTrashOutline size={14} /></button>
                      </div>
                    ))}
                  </div>

                  <div className={styles.cardFooter}>
                    {hasPending ? (
                      <>
                        <button className={styles.saveBtn} onClick={() => handleSaveOrder(branch.id)}>Save Order</button>
                        <button className={styles.cancelBtnSm} onClick={() => cancelReorder(branch.id)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className={styles.addStopsBtn} onClick={() => setShowWaypointPicker(branch.id)}>+ Add Stops</button>
                        <button className={styles.optimizeBtn} onClick={() => handleOptimize(branch.id)}><IoFlashOutline size={14} /> Optimize</button>
                      </>
                    )}
                  </div>

                  {showWaypointPicker === branch.id && (
                    <SalespointPicker
                      salesPoints={contextSalesPoints}
                      excludeIds={new Set((branch.waypoints || []).map(w => w.salespoint?.id).filter(Boolean))}
                      onSubmit={ids => handleAddWaypoints(branch.id, ids)}
                      onCancel={() => setShowWaypointPicker(null)}
                      onRowClick={sp => {
                        setSelectedSP(sp);
                        const lat = parseFloat(sp.lat || sp.latitude);
                        const lng = parseFloat(sp.lng || sp.longitude);
                        if (!isNaN(lat) && !isNaN(lng)) { setMapCenter({ lat, lng }); setMapZoom(16); }
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Right: map ── */}
      <div className={styles.routesMapPanel}>
        {!selectedRouteId || !isLoaded ? (
          <div className={styles.mapPlaceholder}>
            <IoLocationOutline size={40} />
            <p>{!selectedRouteId ? 'Select a route to view branches on map' : 'Loading map…'}</p>
          </div>
        ) : (
          <>
            <div className={styles.mapToggleBar}>
              <span className={styles.mapRouteLabel}>
                <span className={styles.mapRouteDot} style={{ background: routeColor }} />
                {routeDetail?.name || '—'}
              </span>
              <div className={styles.mapToggles}>
                <button className={`${styles.mapToggleBtn} ${showSalespoints ? styles.mapToggleActive : ''}`} onClick={() => setShowSalespoints(v => !v)}>
                  Salespoints
                </button>
                <button className={`${styles.mapToggleBtn} ${showLabels ? styles.mapToggleActive : ''}`} onClick={() => setShowLabels(v => !v)}>
                  Labels
                </button>
                {loadingMap && <span className={styles.mapLoadingBadge}>Loading…</span>}
              </div>
            </div>

            <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {(pickingForkPoint || pickingExitPoint) && (
                <div className={styles.pickingBanner}>
                  <IoLocationOutline size={16} />
                  {pickingForkPoint ? 'Click map to place the fork point' : 'Click map to place the exit point'} — press Esc to cancel
                </div>
              )}
              <GoogleMap
                mapContainerStyle={{ ...MAP_CONTAINER_STYLE, cursor: (pickingForkPoint || pickingExitPoint) ? 'crosshair' : undefined }}
                center={mapCenter}
                zoom={mapZoom}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                onClick={(pickingForkPoint || pickingExitPoint) ? (e) => {
                  const lat = e.latLng.lat().toFixed(6);
                  const lng = e.latLng.lng().toFixed(6);
                  if (pickingForkPoint) {
                    setFormData(p => ({ ...p, fork_point_lat: lat, fork_point_lng: lng }));
                    setPickingForkPoint(false);
                  } else {
                    setFormData(p => ({ ...p, exit_point_lat: lat, exit_point_lng: lng }));
                    setPickingExitPoint(false);
                  }
                  setShowForm(true);
                } : undefined}
              >
              {/* Main route path */}
              {mainDirections.length > 0
                ? mainDirections.map((dir, i) => (
                    <DirectionsRenderer key={`bmain-${i}`} directions={dir}
                      options={{ suppressMarkers: true, polylineOptions: { strokeColor: routeColor, strokeWeight: MAIN_STROKE, strokeOpacity: 0.9, zIndex: 1 } }} />
                  ))
                : routeDetail?.path_coordinates?.length > 1 && (
                    <Polyline path={routeDetail.path_coordinates}
                      options={{ strokeColor: routeColor, strokeWeight: MAIN_STROKE, strokeOpacity: 0.55, zIndex: 1 }} />
                  )
              }

              {/* Route waypoint dots */}
              {(routeDetail?.waypoints || []).map(wp => {
                if (!wp.salespoint?.lat || !wp.salespoint?.lng) return null;
                return (
                  <OverlayView key={wp.id}
                    position={{ lat: Number(wp.salespoint.lat), lng: Number(wp.salespoint.lng) }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                    <div className={styles.wpMarkerWrap}>
                      <div className={styles.waypointDot} style={{ background: routeColor }}
                        onClick={() => { const full = contextSalesPoints.find(s => s.id === wp.salespoint.id) || salesPoints.find(s => s.id === wp.salespoint.id); if (full) setSelectedSP(full); }} />
                      {showLabels && <span className={styles.wpLabel}>{wp.sequence_order}. {wp.salespoint.name}</span>}
                    </div>
                  </OverlayView>
                );
              })}

              {/* Branch polylines */}
              {subroutes.map(sr => {
                if (hiddenBranchIds.has(sr.id)) return null;
                const color = branchColors[sr.id] || sr.color_hex || '#6B21A8';
                const dirs = subrouteDirections[sr.id];
                if (dirs?.length > 0) {
                  return dirs.map((d, i) => (
                    <DirectionsRenderer key={`bsr-${sr.id}-${i}`} directions={d}
                      options={{ suppressMarkers: true, polylineOptions: { strokeColor: color, strokeWeight: BRANCH_STROKE, strokeOpacity: 0.9, zIndex: 2 } }} />
                  ));
                }
                return null; // no straight-line fallback
              })}

              {/* Fork point preview pin */}
              {formData.fork_point_lat && formData.fork_point_lng && !isNaN(parseFloat(formData.fork_point_lat)) && (
                <OverlayView
                  position={{ lat: parseFloat(formData.fork_point_lat), lng: parseFloat(formData.fork_point_lng) }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                  <div className={styles.epMarker}>
                    <div className={styles.epDotPreview} title="Fork point" />
                  </div>
                </OverlayView>
              )}

              {/* Exit point preview pin */}
              {formData.exit_point_lat && formData.exit_point_lng && !isNaN(parseFloat(formData.exit_point_lat)) && (
                <OverlayView
                  position={{ lat: parseFloat(formData.exit_point_lat), lng: parseFloat(formData.exit_point_lng) }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                  <div className={styles.epMarker}>
                    <div className={styles.epDotPreview} title="Exit point" style={{ background: '#EF4444' }} />
                  </div>
                </OverlayView>
              )}

              {showSalespoints && <SalespointsLayer salesPoints={contextSalesPoints} showLabels={showLabels} onSelect={setSelectedSP} />}
            </GoogleMap>
            </div>

            {/* Legend */}
            {subroutes.length > 0 && (
              <div className={styles.mapLegend}>
                <div className={styles.legendRow}>
                  <span className={styles.legendLine} style={{ background: routeColor }} />
                  <span className={styles.legendLabel}>{routeDetail?.code} — {routeDetail?.name}</span>
                </div>
                <div className={styles.legendDivider} />
                <div className={styles.legendGroupTitle}>Branches</div>
                {subroutes.map(sr => {
                  const color = branchColors[sr.id] || sr.color_hex || '#6B21A8';
                  const hidden = hiddenBranchIds.has(sr.id);
                  return (
                    <div key={sr.id} className={`${styles.legendRow} ${hidden ? styles.legendRowHidden : ''}`}>
                      <label className={styles.colorSwatch} title="Change colour">
                        <span style={{ background: color }} />
                        <input type="color" value={color} onChange={e => handleBranchColorChange(sr.id, e.target.value)} className={styles.hiddenColorInput} />
                      </label>
                      <span className={styles.legendLabel}>{sr.code}{sr.name ? ` — ${sr.name}` : ''}</span>
                      <button className={styles.eyeBtn} onClick={() => toggleBranchVisibility(sr.id)}>
                        {hidden ? <IoEyeOffOutline size={13} /> : <IoEyeOutline size={13} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedSP && (
              <SalespointInfoPopup
                sp={selectedSP}
                onClose={() => setSelectedSP(null)}
                routes={routes}
                subroutes={subroutes}
                corridors={[]}
                currentRouteId={selectedRouteId}
                onAddDone={() => loadBranchesData(selectedRouteId)}
              />
            )}
          </>
        )}
      </div>

      {/* Branch form modal */}
      {showForm && (
        <div className={styles.modalOverlay}>
          <form className={styles.modal} onSubmit={handleSubmit}>
            <div className={styles.modalHeader}>
              <h3>{editingBranch ? 'Edit Branch' : 'New Branch'}</h3>
              <button type="button" className={styles.closeBtn} onClick={() => setShowForm(false)}><IoCloseOutline size={20} /></button>
            </div>

            {!editingBranch && (
              <div className={styles.formGroup}>
                <label>Parent Route *</label>
                <select required className={styles.input} value={formRouteId} onChange={e => setFormRouteId(e.target.value)}>
                  <option value="">— Select route —</option>
                  {routes.map(r => <option key={r.id} value={r.id}>{r.code} — {r.name}</option>)}
                </select>
              </div>
            )}

            <div className={styles.formGrid2}>
              <div className={styles.formGroup}>
                <label>Code *</label>
                <input required className={styles.input} value={formData.code}
                  onChange={e => setFormData(p => ({ ...p, code: e.target.value }))} placeholder="e.g. R1-A" />
              </div>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input className={styles.input} value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Branch name" />
              </div>
              <div className={styles.formGroup}>
                <label>Branch Type</label>
                <select className={styles.input} value={formData.branch_type}
                  onChange={e => setFormData(p => ({ ...p, branch_type: e.target.value }))}>
                  {BRANCH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Color</label>
                <div className={styles.colorRow}>
                  <input type="color" className={styles.colorInput} value={formData.color_hex}
                    onChange={e => setFormData(p => ({ ...p, color_hex: e.target.value }))} />
                  <span className={styles.colorLabel}>{formData.color_hex}</span>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Corridor (m)</label>
                <input type="number" className={styles.input} value={formData.corridor_meters}
                  onChange={e => setFormData(p => ({ ...p, corridor_meters: parseInt(e.target.value) || 100 }))} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.checkLabel}>
                  <input type="checkbox" checked={formData.independently_serviceable}
                    onChange={e => setFormData(p => ({ ...p, independently_serviceable: e.target.checked }))} />
                  Independently Serviceable
                </label>
              </div>
            </div>

            <div className={styles.formGroup}>
              <div className={styles.labelRow}>
                <label>Fork Point <span className={styles.hint}>(GPS where branch leaves parent road)</span></label>
                <button type="button" className={styles.pickMapBtn} onClick={() => { setPickingForkPoint(true); setShowForm(false); }}>
                  <IoLocationOutline size={13} /> Pick from map
                </button>
              </div>
              <div className={styles.coordRow}>
                <input type="number" step="any" className={styles.input} placeholder="Latitude"
                  value={formData.fork_point_lat} onChange={e => setFormData(p => ({ ...p, fork_point_lat: e.target.value }))} />
                <input type="number" step="any" className={styles.input} placeholder="Longitude"
                  value={formData.fork_point_lng} onChange={e => setFormData(p => ({ ...p, fork_point_lng: e.target.value }))} />
              </div>
              {formData.fork_point_lat && formData.fork_point_lng && (
                <div className={styles.coordPreview}>
                  <IoLocationOutline size={12} /> {parseFloat(formData.fork_point_lat).toFixed(5)}, {parseFloat(formData.fork_point_lng).toFixed(5)}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <div className={styles.labelRow}>
                <label>Exit Point <span className={styles.hint}>(GPS at branch far end)</span></label>
                <button type="button" className={styles.pickMapBtn} onClick={() => { setPickingExitPoint(true); setShowForm(false); }}>
                  <IoLocationOutline size={13} /> Pick from map
                </button>
              </div>
              <div className={styles.coordRow}>
                <input type="number" step="any" className={styles.input} placeholder="Latitude"
                  value={formData.exit_point_lat} onChange={e => setFormData(p => ({ ...p, exit_point_lat: e.target.value }))} />
                <input type="number" step="any" className={styles.input} placeholder="Longitude"
                  value={formData.exit_point_lng} onChange={e => setFormData(p => ({ ...p, exit_point_lng: e.target.value }))} />
              </div>
              {formData.exit_point_lat && formData.exit_point_lng && (
                <div className={styles.coordPreview}>
                  <IoLocationOutline size={12} /> {parseFloat(formData.exit_point_lat).toFixed(5)}, {parseFloat(formData.exit_point_lng).toFixed(5)}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label>Parent Branch <span className={styles.hint}>(if this forks off another branch)</span></label>
              <select className={styles.input} value={formData.parent_branch || ''}
                onChange={e => setFormData(p => ({ ...p, parent_branch: e.target.value || null }))}>
                <option value="">— None (forks off mainline) —</option>
                {branchOptions.map(b => <option key={b.id} value={b.id}>{b.code}{b.name ? ` — ${b.name}` : ''}</option>)}
              </select>
            </div>

            {formData.branch_type === 'branch_connector' && (
              <div className={styles.formGroup}>
                <label>Connects To <span className={styles.hint}>(branch at exit point)</span></label>
                <select className={styles.input} value={formData.exit_to_subroute || ''}
                  onChange={e => setFormData(p => ({ ...p, exit_to_subroute: e.target.value || null }))}>
                  <option value="">— Select target branch —</option>
                  {branchOptions.map(b => <option key={b.id} value={b.id}>{b.code}{b.name ? ` — ${b.name}` : ''}</option>)}
                </select>
              </div>
            )}

            <div className={styles.modalFooter}>
              <button type="button" className={styles.cancelBtnSm} onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className={styles.saveBtn} disabled={loading}>
                {editingBranch ? 'Save Changes' : 'Create Branch'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ── ExitCorridorsSection ──────────────────────────────────────────────────────

function ExitCorridorsSection({ depots, routes, salesPoints }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_API_KEY });

  const [corridors, setCorridors] = useState([]);
  const [selectedDepotId, setSelectedDepotId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCorridor, setEditingCorridor] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_CORRIDOR_FORM });
  const [showWaypointPicker, setShowWaypointPicker] = useState(null);
  const [reordered, setReordered] = useState({});
  const [pendingReorder, setPendingReorder] = useState({});
  const [dragged, setDragged] = useState(null);

  // Map pick state
  const [pickingDivergencePoint, setPickingDivergencePoint] = useState(false);

  // Map state
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(12);
  const [showSalespoints, setShowSalespoints] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [selectedSP, setSelectedSP] = useState(null);
  const [corridorDirections, setCorridorDirections] = useState({});

  const loadCorridors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await exitCorridorsAPI.list(selectedDepotId || null);
      setCorridors(res.data?.results || res.data || []);
    } catch { setError('Failed to load exit corridors'); }
    finally { setLoading(false); }
  }, [selectedDepotId]);

  useEffect(() => { loadCorridors(); }, [loadCorridors]);

  useEffect(() => {
    if (!pickingDivergencePoint) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setPickingDivergencePoint(false); setShowForm(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickingDivergencePoint]);

  // Recalculate map center + directions when corridors list changes
  useEffect(() => {
    setCorridorDirections({});
    if (corridors.length === 0) return;
    for (const c of corridors) {
      const pts = buildCorridorPath(c, c.depot);
      if (pts.length > 0) { setMapCenter(pts[0]); setMapZoom(13); break; }
    }
    corridors.forEach(c => {
      const pts = buildCorridorPath(c, c.depot);
      if (pts.length < 2) return;
      calculateDirectionsForPoints(pts).then(dirs => {
        if (dirs.length > 0) setCorridorDirections(prev => ({ ...prev, [c.id]: dirs }));
      });
    });
  }, [corridors]);

  const openCreateForm = () => {
    setFormData({ ...DEFAULT_CORRIDOR_FORM, depot_id: selectedDepotId || '' });
    setEditingCorridor(null);
    setShowForm(true);
  };

  const openEditForm = (corridor) => {
    setFormData({
      name: corridor.name || '',
      depot_id: String(corridor.depot),
      corridor_meters: corridor.corridor_meters || 150,
      is_active: corridor.is_active !== false,
      divergence_point_lat: corridor.divergence_point_coords?.lat ?? '',
      divergence_point_lng: corridor.divergence_point_coords?.lng ?? '',
      route_ids: corridor.route_ids || [],
    });
    setEditingCorridor(corridor);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.depot_id) { setError('Please select a depot'); return; }
    const payload = {
      ...formData,
      divergence_point_lat: formData.divergence_point_lat !== '' ? parseFloat(formData.divergence_point_lat) : null,
      divergence_point_lng: formData.divergence_point_lng !== '' ? parseFloat(formData.divergence_point_lng) : null,
    };
    try {
      setLoading(true);
      if (editingCorridor) await exitCorridorsAPI.patch(editingCorridor.id, payload);
      else await exitCorridorsAPI.create(payload);
      setShowForm(false);
      loadCorridors();
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save corridor');
    } finally { setLoading(false); }
  };

  const handleDelete = async (corridor) => {
    if (!window.confirm(`Delete corridor "${corridor.name}"?`)) return;
    try {
      setLoading(true);
      await exitCorridorsAPI.delete(corridor.id);
      loadCorridors();
    } catch { setError('Failed to delete corridor'); }
    finally { setLoading(false); }
  };

  const toggleRouteId = (routeId) => {
    setFormData(p => ({
      ...p,
      route_ids: p.route_ids.includes(routeId)
        ? p.route_ids.filter(id => id !== routeId)
        : [...p.route_ids, routeId],
    }));
  };

  const handleAddWaypoints = async (corridorId, orderedIds) => {
    try {
      setLoading(true);
      await exitCorridorsAPI.addWaypointsBulk(corridorId, { salespoint_ids: orderedIds });
      setShowWaypointPicker(null);
      loadCorridors();
    } catch { setError('Failed to add stops'); }
    finally { setLoading(false); }
  };

  const handleRemoveWaypoint = async (corridorId, waypointId) => {
    try {
      setLoading(true);
      await exitCorridorsAPI.removeWaypoint(corridorId, waypointId);
      loadCorridors();
    } catch { setError('Failed to remove stop'); }
    finally { setLoading(false); }
  };

  const handleDragStart = (e, wp, corridorId) => {
    setDragged({ ...wp, corridorId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetWp, corridorId) => {
    e.preventDefault();
    if (!dragged || dragged.id === targetWp.id) return;
    const corridor = corridors.find(c => c.id === corridorId);
    if (!corridor) return;
    const current = reordered[corridorId] || [...(corridor.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);
    const dIdx = current.findIndex(w => w.id === dragged.id);
    const tIdx = current.findIndex(w => w.id === targetWp.id);
    if (dIdx === -1 || tIdx === -1) return;
    const next = [...current];
    const [removed] = next.splice(dIdx, 1);
    next.splice(tIdx, 0, removed);
    setReordered(prev => ({ ...prev, [corridorId]: next }));
    setPendingReorder(prev => ({ ...prev, [corridorId]: true }));
  };

  const handleSaveOrder = async (corridorId) => {
    const newOrder = reordered[corridorId];
    if (!newOrder) return;
    try {
      setLoading(true);
      await exitCorridorsAPI.reorderWaypoints(corridorId, newOrder.map(w => w.id));
      setPendingReorder(prev => ({ ...prev, [corridorId]: false }));
      setReordered(prev => { const u = { ...prev }; delete u[corridorId]; return u; });
      loadCorridors();
    } catch { setError('Failed to save order'); }
    finally { setLoading(false); }
  };

  const cancelReorder = (corridorId) => {
    setReordered(prev => { const u = { ...prev }; delete u[corridorId]; return u; });
    setPendingReorder(prev => ({ ...prev, [corridorId]: false }));
  };

  const getWaypoints = (corridor) =>
    reordered[corridor.id] || [...(corridor.waypoints || [])].sort((a, b) => a.sequence_order - b.sequence_order);

  const depotName = (depotId) => {
    const d = depots.find(d => String(d.id) === String(depotId));
    return d?.name || d?.entity_name || depotId;
  };

  const contextSalesPoints = selectedDepotId
    ? salesPoints.filter(sp => (sp.reference_entity || []).some(e => String(e.id) === String(selectedDepotId)))
    : salesPoints;

  return (
    <div className={styles.sectionSplit}>
      {/* ── Left: corridor list ── */}
      <div className={styles.sectionList}>
        <ErrorBar message={error} onClose={() => setError(null)} />
        <div className={styles.sectionToolbar}>
          <select className={styles.filterSelect} value={selectedDepotId} onChange={e => setSelectedDepotId(e.target.value)}>
            <option value="">All Depots</option>
            {depots.map(d => <option key={d.id} value={d.id}>{d.name || d.entity_name}</option>)}
          </select>
          <button className={styles.iconBtn} onClick={loadCorridors} title="Refresh"><IoRefreshOutline size={16} /></button>
          <button className={styles.addBtn} onClick={openCreateForm}><IoAddOutline size={16} /> New Corridor</button>
        </div>

        {loading && <div className={styles.loadingBar}>Loading...</div>}
        {!loading && corridors.length === 0 && (
          <div className={styles.empty}>No exit corridors defined.</div>
        )}

        {corridors.map(corridor => {
          const isExpanded = expandedId === corridor.id;
          const waypoints = getWaypoints(corridor);
          const hasPending = !!pendingReorder[corridor.id];
          return (
            <div key={corridor.id} className={styles.card} style={{ borderLeftColor: '#EF4444' }}>
              <div className={styles.cardHeader}>
                <IoLocationOutline size={16} className={styles.corridorIcon} />
                <span className={styles.cardCode}>{corridor.name}</span>
                <span className={styles.cardMeta}>{depotName(corridor.depot)}</span>
                <span className={styles.cardMeta}>{corridor.waypoint_count || 0} stops</span>
                <div className={styles.cardActions}>
                  <button className={styles.iconBtn} onClick={() => setExpandedId(isExpanded ? null : corridor.id)}>
                    {isExpanded ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                  </button>
                  <button className={styles.iconBtn} onClick={() => openEditForm(corridor)}><IoCreateOutline size={16} /></button>
                  <button className={`${styles.iconBtn} ${styles.danger}`} onClick={() => handleDelete(corridor)}><IoTrashOutline size={16} /></button>
                </div>
              </div>

              {isExpanded && (
                <div className={styles.cardBody}>
                  {corridor.route_ids?.length > 0 && (
                    <div className={styles.metaItem} style={{ marginBottom: 8 }}>
                      <strong>Routes: </strong>
                      {corridor.route_ids.map(rid => {
                        const r = routes.find(r => String(r.id) === String(rid));
                        return r ? <span key={rid} className={styles.routeTag}>{r.code}</span> : null;
                      })}
                    </div>
                  )}
                  {corridor.divergence_point_coords && (
                    <div className={styles.metaItem} style={{ marginBottom: 8 }}>
                      <strong>Divergence: </strong>{corridor.divergence_point_coords.lat.toFixed(5)}, {corridor.divergence_point_coords.lng.toFixed(5)}
                    </div>
                  )}

                  <div className={styles.waypointList}>
                    {waypoints.length === 0 && <div className={styles.emptySmall}>No stops — add some below.</div>}
                    {waypoints.map((wp, idx) => (
                      <div key={wp.id} className={styles.waypointRow}
                        draggable onDragStart={e => handleDragStart(e, wp, corridor.id)}
                        onDragOver={e => handleDragOver(e, wp, corridor.id)}
                        onDragEnd={() => setDragged(null)}>
                        <IoReorderThreeOutline size={16} className={styles.dragHandle} />
                        <span className={styles.wpNum}>{idx + 1}</span>
                        <span className={styles.wpName}>{wp.salespoint?.name || '—'}</span>
                        <button className={`${styles.iconBtn} ${styles.danger}`} onClick={() => handleRemoveWaypoint(corridor.id, wp.id)}><IoTrashOutline size={14} /></button>
                      </div>
                    ))}
                  </div>

                  <div className={styles.cardFooter}>
                    {hasPending ? (
                      <>
                        <button className={styles.saveBtn} onClick={() => handleSaveOrder(corridor.id)}>Save Order</button>
                        <button className={styles.cancelBtnSm} onClick={() => cancelReorder(corridor.id)}>Cancel</button>
                      </>
                    ) : (
                      <button className={styles.addStopsBtn} onClick={() => setShowWaypointPicker(corridor.id)}>+ Add Salespoints</button>
                    )}
                  </div>

                  {showWaypointPicker === corridor.id && (
                    <SalespointPicker
                      salesPoints={contextSalesPoints}
                      excludeIds={new Set((corridor.waypoints || []).map(w => w.salespoint?.id).filter(Boolean))}
                      onSubmit={ids => handleAddWaypoints(corridor.id, ids)}
                      onCancel={() => setShowWaypointPicker(null)}
                      onRowClick={sp => {
                        setSelectedSP(sp);
                        const lat = parseFloat(sp.lat || sp.latitude);
                        const lng = parseFloat(sp.lng || sp.longitude);
                        if (!isNaN(lat) && !isNaN(lng)) { setMapCenter({ lat, lng }); setMapZoom(16); }
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Right: map ── */}
      <div className={styles.routesMapPanel}>
        {!isLoaded ? (
          <div className={styles.mapPlaceholder}><p>Loading map…</p></div>
        ) : corridors.length === 0 && !pickingDivergencePoint ? (
          <div className={styles.mapPlaceholder}>
            <IoLocationOutline size={40} />
            <p>No corridors to display</p>
          </div>
        ) : (
          <>
            <div className={styles.mapToggleBar}>
              <span className={styles.mapRouteLabel}>Exit Corridors</span>
              <div className={styles.mapToggles}>
                <button className={`${styles.mapToggleBtn} ${showSalespoints ? styles.mapToggleActive : ''}`} onClick={() => setShowSalespoints(v => !v)}>
                  Salespoints
                </button>
                <button className={`${styles.mapToggleBtn} ${showLabels ? styles.mapToggleActive : ''}`} onClick={() => setShowLabels(v => !v)}>
                  Labels
                </button>
              </div>
            </div>

            <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {pickingDivergencePoint && (
                <div className={styles.pickingBanner}>
                  <IoLocationOutline size={16} />
                  Click map to place the divergence point — press Esc to cancel
                </div>
              )}
              <GoogleMap
                mapContainerStyle={{ ...MAP_CONTAINER_STYLE, cursor: pickingDivergencePoint ? 'crosshair' : undefined }}
                center={mapCenter}
                zoom={mapZoom}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                onClick={pickingDivergencePoint ? (e) => {
                  const lat = e.latLng.lat().toFixed(6);
                  const lng = e.latLng.lng().toFixed(6);
                  setFormData(p => ({ ...p, divergence_point_lat: lat, divergence_point_lng: lng }));
                  setPickingDivergencePoint(false);
                  setShowForm(true);
                } : undefined}
              >
              {corridors.map(c => {
                const dirs = corridorDirections[c.id];
                if (dirs?.length > 0) {
                  return dirs.map((d, i) => (
                    <DirectionsRenderer key={`ec-${c.id}-${i}`} directions={d}
                      options={{ suppressMarkers: true, polylineOptions: { strokeColor: '#EF4444', strokeWeight: MAIN_STROKE, strokeOpacity: 0.85, zIndex: 1 } }} />
                  ));
                }
                return null; // no straight-line fallback
              })}

              {/* Corridor waypoint dots (red squares) */}
              {corridors.flatMap(c =>
                (c.waypoints || [])
                  .filter(w => w.salespoint?.lat && w.salespoint?.lng)
                  .map(w => (
                    <OverlayView key={w.id}
                      position={{ lat: Number(w.salespoint.lat), lng: Number(w.salespoint.lng) }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                      <div className={styles.corridorWpMarkerWrap}>
                        <div className={styles.corridorWpDot}
                          onClick={() => { const full = contextSalesPoints.find(s => s.id === w.salespoint.id) || salesPoints.find(s => s.id === w.salespoint.id); if (full) setSelectedSP(full); }}
                          title={w.salespoint.name} />
                        {showLabels && <span className={styles.wpLabel}>{w.sequence_order}. {w.salespoint.name}</span>}
                      </div>
                    </OverlayView>
                  ))
              )}

              {/* Divergence point markers (orange diamonds) */}
              {corridors.filter(c => c.divergence_point_coords?.lat).map(c => (
                <OverlayView key={`div-${c.id}`}
                  position={{ lat: Number(c.divergence_point_coords.lat), lng: Number(c.divergence_point_coords.lng) }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                  <div className={styles.divergenceMarkerWrap}>
                    <div className={styles.divergenceMarker} title={`${c.name} — divergence point`} />
                    {showLabels && <span className={styles.divergenceLabel}>{c.name} split</span>}
                  </div>
                </OverlayView>
              ))}

              {/* Divergence point preview pin */}
              {formData.divergence_point_lat && formData.divergence_point_lng && !isNaN(parseFloat(formData.divergence_point_lat)) && (
                <OverlayView
                  position={{ lat: parseFloat(formData.divergence_point_lat), lng: parseFloat(formData.divergence_point_lng) }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                  <div className={styles.divergenceMarkerWrap}>
                    <div className={styles.divergenceMarker} title="Divergence point" style={{ opacity: 0.7 }} />
                  </div>
                </OverlayView>
              )}

              {showSalespoints && <SalespointsLayer salesPoints={contextSalesPoints} showLabels={showLabels} onSelect={setSelectedSP} />}
            </GoogleMap>
            </div>

            {/* Legend */}
            <div className={styles.mapLegend}>
              <div className={styles.legendGroupTitle}>Exit Corridors</div>
              {corridors.map(c => (
                <div key={c.id} className={styles.legendRow}>
                  <span className={styles.legendLine} style={{ background: '#EF4444' }} />
                  <span className={styles.legendLabel}>{c.name}</span>
                  <span className={styles.cardMeta} style={{ fontSize: 10 }}>{c.waypoint_count || 0}pts</span>
                </div>
              ))}
              <div className={styles.legendDivider} />
              <div className={styles.legendRow}>
                <span className={styles.legendCorridorWpSwatch} />
                <span className={styles.legendLabel}>Corridor stop</span>
              </div>
              <div className={styles.legendRow}>
                <span className={styles.legendDivergenceSwatch} />
                <span className={styles.legendLabel}>Divergence point</span>
              </div>
            </div>

            {selectedSP && (
              <SalespointInfoPopup
                sp={selectedSP}
                onClose={() => setSelectedSP(null)}
                routes={routes}
                subroutes={[]}
                corridors={corridors}
                onAddDone={loadCorridors}
              />
            )}
          </>
        )}
      </div>

      {/* Corridor form modal */}
      {showForm && (
        <div className={styles.modalOverlay}>
          <form className={styles.modal} onSubmit={handleSubmit}>
            <div className={styles.modalHeader}>
              <h3>{editingCorridor ? 'Edit Exit Corridor' : 'New Exit Corridor'}</h3>
              <button type="button" className={styles.closeBtn} onClick={() => setShowForm(false)}><IoCloseOutline size={20} /></button>
            </div>

            <div className={styles.formGrid2}>
              <div className={`${styles.formGroup} ${styles.spanFull}`}>
                <label>Name *</label>
                <input required className={styles.input} value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Left Exit" />
              </div>
              <div className={styles.formGroup}>
                <label>Depot *</label>
                <select required className={styles.input} value={formData.depot_id}
                  onChange={e => setFormData(p => ({ ...p, depot_id: e.target.value }))}>
                  <option value="">— Select depot —</option>
                  {depots.map(d => <option key={d.id} value={d.id}>{d.name || d.entity_name}</option>)}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Corridor Width (m)</label>
                <input type="number" className={styles.input} value={formData.corridor_meters}
                  onChange={e => setFormData(p => ({ ...p, corridor_meters: parseInt(e.target.value) || 150 }))} />
              </div>
            </div>

            <div className={styles.formGroup}>
              <div className={styles.labelRow}>
                <label>Divergence Point <span className={styles.hint}>(GPS where routes split from this corridor)</span></label>
                <button type="button" className={styles.pickMapBtn} onClick={() => { setPickingDivergencePoint(true); setShowForm(false); }}>
                  <IoLocationOutline size={13} /> Pick from map
                </button>
              </div>
              <div className={styles.coordRow}>
                <input type="number" step="any" className={styles.input} placeholder="Latitude"
                  value={formData.divergence_point_lat} onChange={e => setFormData(p => ({ ...p, divergence_point_lat: e.target.value }))} />
                <input type="number" step="any" className={styles.input} placeholder="Longitude"
                  value={formData.divergence_point_lng} onChange={e => setFormData(p => ({ ...p, divergence_point_lng: e.target.value }))} />
              </div>
              {formData.divergence_point_lat && formData.divergence_point_lng && (
                <div className={styles.coordPreview}>
                  <IoLocationOutline size={12} /> {parseFloat(formData.divergence_point_lat).toFixed(5)}, {parseFloat(formData.divergence_point_lng).toFixed(5)}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label>Routes Using This Corridor</label>
              <div className={styles.checkList}>
                {routes.map(r => (
                  <label key={r.id} className={styles.checkItem}>
                    <input type="checkbox" checked={formData.route_ids.includes(r.id)}
                      onChange={() => toggleRouteId(r.id)} />
                    <span className={styles.colorDot} style={{ background: r.color_hex || '#999' }} />
                    {r.code} — {r.name}
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button type="button" className={styles.cancelBtnSm} onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className={styles.saveBtn} disabled={loading}>
                {editingCorridor ? 'Save Changes' : 'Create Corridor'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ── EntryPointsSection ────────────────────────────────────────────────────────

function EntryPointsSection({ routes, salesPoints }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_API_KEY });

  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [entryPoints, setEntryPoints] = useState([]);
  const [routeWaypoints, setRouteWaypoints] = useState([]);
  const [routeDetail, setRouteDetail] = useState(null);
  const [corridors, setCorridors] = useState([]);
  const [subroutes, setSubroutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEP, setEditingEP] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_EP_FORM });
  const [pickingLocation, setPickingLocation] = useState(false);

  // Map state
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(12);
  const [showSalespoints, setShowSalespoints] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [selectedSP, setSelectedSP] = useState(null);
  const [mainDirections, setMainDirections] = useState([]);

  const loadEntryPoints = useCallback(async (routeId) => {
    try {
      setLoading(true);
      setMainDirections([]);
      const [epRes, wpRes, srRes, corrRes] = await Promise.all([
        routeEntryPointsAPI.list(routeId),
        routesAPI.get(routeId),
        subRoutesAPI.list(routeId),
        exitCorridorsAPI.list(null),
      ]);
      setEntryPoints(epRes.data?.results || epRes.data || []);
      const detail = wpRes.data;
      setRouteDetail(detail);
      setRouteWaypoints(detail?.waypoints || []);
      setSubroutes(srRes.data?.results || srRes.data || []);
      setCorridors(corrRes.data?.results || corrRes.data || []);

      const path = detail?.path_coordinates || [];
      if (path.length > 0) {
        const mid = path[Math.floor(path.length / 2)];
        setMapCenter({ lat: Number(mid.lat), lng: Number(mid.lng) });
        setMapZoom(13);
      }

      const mainPts = (detail?.waypoints || [])
        .filter(wp => wp.salespoint?.lat && wp.salespoint?.lng)
        .sort((a, b) => a.sequence_order - b.sequence_order)
        .map(wp => ({ lat: Number(wp.salespoint.lat), lng: Number(wp.salespoint.lng) }));
      calculateDirectionsForPoints(mainPts).then(dirs => {
        if (dirs.length > 0) setMainDirections(dirs);
      });
    } catch { setError('Failed to load entry points'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedRouteId) loadEntryPoints(selectedRouteId);
    else { setEntryPoints([]); setRouteWaypoints([]); setRouteDetail(null); }
  }, [selectedRouteId, loadEntryPoints]);

  useEffect(() => {
    if (!pickingLocation) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setPickingLocation(false); setShowForm(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickingLocation]);

  const openCreateForm = () => { setFormData({ ...DEFAULT_EP_FORM }); setEditingEP(null); setShowForm(true); };

  const openEditForm = (ep) => {
    setFormData({
      name: ep.name || '',
      entry_type: ep.entry_type || 'primary',
      location_lat: ep.location_coords?.lat ?? '',
      location_lng: ep.location_coords?.lng ?? '',
      entry_radius_meters: ep.entry_radius_meters || 200,
      from_exit_corridor: ep.from_exit_corridor || '',
      from_route: ep.from_route || '',
      from_subroute: ep.from_subroute || '',
      nearest_waypoint_id: ep.nearest_waypoint || '',
      default_direction: ep.default_direction || 'forward',
      is_active: ep.is_active !== false,
    });
    setEditingEP(ep);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRouteId) { setError('Please select a route'); return; }
    const payload = {
      ...formData,
      from_exit_corridor: formData.from_exit_corridor || null,
      from_route: formData.from_route || null,
      from_subroute: formData.from_subroute || null,
      nearest_waypoint_id: formData.nearest_waypoint_id || null,
      location_lat: parseFloat(formData.location_lat),
      location_lng: parseFloat(formData.location_lng),
    };
    try {
      setLoading(true);
      if (editingEP) await routeEntryPointsAPI.patch(editingEP.id, payload);
      else await routeEntryPointsAPI.create(selectedRouteId, payload);
      setShowForm(false);
      loadEntryPoints(selectedRouteId);
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save entry point');
    } finally { setLoading(false); }
  };

  const handleDelete = async (ep) => {
    if (!window.confirm(`Delete entry point "${ep.name}"?`)) return;
    try {
      setLoading(true);
      await routeEntryPointsAPI.delete(ep.id);
      loadEntryPoints(selectedRouteId);
    } catch { setError('Failed to delete entry point'); }
    finally { setLoading(false); }
  };

  const entryTypeBadgeClass = (type) => {
    if (type === 'primary') return styles.badgePrimary;
    if (type === 'from_route') return styles.badgeRoute;
    return styles.badgeBranch;
  };

  const otherRoutes = routes.filter(r => String(r.id) !== String(selectedRouteId));
  const routeColor = routeDetail?.color_hex || '#2196F3';

  return (
    <div className={styles.sectionSplit}>
      {/* ── Left: entry points list ── */}
      <div className={styles.sectionList}>
        <ErrorBar message={error} onClose={() => setError(null)} />
        <div className={styles.sectionToolbar}>
          <select className={styles.filterSelect} value={selectedRouteId} onChange={e => setSelectedRouteId(e.target.value)}>
            <option value="">— Select a route —</option>
            {routes.map(r => <option key={r.id} value={r.id}>{r.code} — {r.name}</option>)}
          </select>
          {selectedRouteId && (
            <button className={styles.addBtn} onClick={openCreateForm}><IoAddOutline size={16} /> New Entry Point</button>
          )}
        </div>

        {loading && <div className={styles.loadingBar}>Loading...</div>}
        {selectedRouteId && !loading && entryPoints.length === 0 && (
          <div className={styles.empty}>No entry points defined for this route.</div>
        )}

        {entryPoints.map(ep => (
          <div key={ep.id} className={styles.card} style={{ borderLeftColor: '#1C3520' }}>
            <div className={styles.cardHeader}>
              <span className={`${styles.badge} ${entryTypeBadgeClass(ep.entry_type)}`}>
                {ENTRY_TYPES.find(t => t.value === ep.entry_type)?.label || ep.entry_type}
              </span>
              <span className={styles.cardCode}>{ep.name}</span>
              {ep.location_coords && (
                <span className={styles.cardMeta}>{ep.location_coords.lat.toFixed(4)}, {ep.location_coords.lng.toFixed(4)}</span>
              )}
              <span className={styles.cardMeta}>r={ep.entry_radius_meters}m</span>
              {ep.nearest_waypoint_display && (
                <span className={styles.cardMeta}>→ {ep.nearest_waypoint_display}</span>
              )}
              <div className={styles.cardActions}>
                <button className={styles.iconBtn} onClick={() => openEditForm(ep)}><IoCreateOutline size={16} /></button>
                <button className={`${styles.iconBtn} ${styles.danger}`} onClick={() => handleDelete(ep)}><IoTrashOutline size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Right: map ── */}
      <div className={styles.routesMapPanel}>
        {!selectedRouteId || !isLoaded ? (
          <div className={styles.mapPlaceholder}>
            <IoLocationOutline size={40} />
            <p>{!selectedRouteId ? 'Select a route to view entry points on map' : 'Loading map…'}</p>
          </div>
        ) : (
          <>
            <div className={styles.mapToggleBar}>
              <span className={styles.mapRouteLabel}>
                <span className={styles.mapRouteDot} style={{ background: routeColor }} />
                {routeDetail?.name || '—'}
              </span>
              <div className={styles.mapToggles}>
                <button className={`${styles.mapToggleBtn} ${showSalespoints ? styles.mapToggleActive : ''}`} onClick={() => setShowSalespoints(v => !v)}>
                  Salespoints
                </button>
                <button className={`${styles.mapToggleBtn} ${showLabels ? styles.mapToggleActive : ''}`} onClick={() => setShowLabels(v => !v)}>
                  Labels
                </button>
              </div>
            </div>

            <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {pickingLocation && (
                <div className={styles.pickingBanner}>
                  <IoLocationOutline size={16} />
                  Click anywhere on the map to place the entry point — press Esc to cancel
                </div>
              )}
              <GoogleMap
                mapContainerStyle={{ ...MAP_CONTAINER_STYLE, cursor: pickingLocation ? 'crosshair' : undefined }}
                center={mapCenter}
                zoom={mapZoom}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                onClick={pickingLocation ? (e) => {
                  const lat = e.latLng.lat().toFixed(6);
                  const lng = e.latLng.lng().toFixed(6);
                  setFormData(p => ({ ...p, location_lat: lat, location_lng: lng }));
                  setPickingLocation(false);
                  setShowForm(true);
                } : undefined}
              >
                {/* Route path */}
                {mainDirections.length > 0
                  ? mainDirections.map((dir, i) => (
                      <DirectionsRenderer key={`ep-main-${i}`} directions={dir}
                        options={{ suppressMarkers: true, polylineOptions: { strokeColor: routeColor, strokeWeight: MAIN_STROKE, strokeOpacity: 0.75, zIndex: 1 } }} />
                    ))
                  : routeDetail?.path_coordinates?.length > 1 && (
                      <Polyline path={routeDetail.path_coordinates}
                        options={{ strokeColor: routeColor, strokeWeight: MAIN_STROKE, strokeOpacity: 0.45, zIndex: 1 }} />
                    )
                }

                {/* Route waypoints */}
                {routeWaypoints.map(wp => {
                  if (!wp.salespoint?.lat || !wp.salespoint?.lng) return null;
                  return (
                    <OverlayView key={wp.id}
                      position={{ lat: Number(wp.salespoint.lat), lng: Number(wp.salespoint.lng) }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                      <div className={styles.wpMarkerWrap}>
                        <div className={styles.waypointDot} style={{ background: routeColor }}
                          onClick={() => { if (pickingLocation) return; const full = salesPoints.find(s => s.id === wp.salespoint.id); if (full) setSelectedSP(full); }} />
                        {showLabels && <span className={styles.wpLabel}>{wp.sequence_order}. {wp.salespoint.name}</span>}
                      </div>
                    </OverlayView>
                  );
                })}

                {/* Entry point pins */}
                {entryPoints.filter(ep => ep.location_coords?.lat).map(ep => (
                  <OverlayView key={ep.id}
                    position={{ lat: Number(ep.location_coords.lat), lng: Number(ep.location_coords.lng) }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                    <div className={styles.epMarker}>
                      <div className={styles.epDot} title={ep.name} />
                      {showLabels && <span className={styles.wpLabel} style={{ marginLeft: 0, marginTop: 2 }}>{ep.name}</span>}
                    </div>
                  </OverlayView>
                ))}

                {/* Preview pin for location being picked */}
                {formData.location_lat && formData.location_lng && !isNaN(parseFloat(formData.location_lat)) && (
                  <OverlayView
                    position={{ lat: parseFloat(formData.location_lat), lng: parseFloat(formData.location_lng) }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                    <div className={styles.epMarker}>
                      <div className={styles.epDotPreview} title="Entry point location" />
                    </div>
                  </OverlayView>
                )}

                {showSalespoints && <SalespointsLayer salesPoints={salesPoints} showLabels={showLabels} onSelect={setSelectedSP} />}
              </GoogleMap>
            </div>

            {/* Legend */}
            {entryPoints.length > 0 && (
              <div className={styles.mapLegend}>
                <div className={styles.legendRow}>
                  <span className={styles.legendLine} style={{ background: routeColor }} />
                  <span className={styles.legendLabel}>{routeDetail?.code} — {routeDetail?.name}</span>
                </div>
                <div className={styles.legendDivider} />
                <div className={styles.legendGroupTitle}>Entry Points</div>
                {entryPoints.map(ep => (
                  <div key={ep.id} className={styles.legendRow}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#1C3520', border: '2px solid #fff', flexShrink: 0, boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} />
                    <span className={styles.legendLabel}>{ep.name}</span>
                  </div>
                ))}
              </div>
            )}

            {selectedSP && (
              <SalespointInfoPopup
                sp={selectedSP}
                onClose={() => setSelectedSP(null)}
                routes={routes}
                subroutes={subroutes}
                corridors={[]}
                currentRouteId={selectedRouteId}
                onAddDone={() => loadEntryPoints(selectedRouteId)}
              />
            )}
          </>
        )}
      </div>

      {/* Entry point form modal */}
      {showForm && (
        <div className={styles.modalOverlay}>
          <form className={styles.modal} onSubmit={handleSubmit}>
            <div className={styles.modalHeader}>
              <h3>{editingEP ? 'Edit Entry Point' : 'New Entry Point'}</h3>
              <button type="button" className={styles.closeBtn} onClick={() => setShowForm(false)}><IoCloseOutline size={20} /></button>
            </div>

            <div className={styles.formGrid2}>
              <div className={`${styles.formGroup} ${styles.spanFull}`}>
                <label>Name *</label>
                <input required className={styles.input} value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Primary from depot" />
              </div>
              <div className={styles.formGroup}>
                <label>Entry Type</label>
                <select className={styles.input} value={formData.entry_type}
                  onChange={e => setFormData(p => ({ ...p, entry_type: e.target.value, from_exit_corridor: '', from_route: '', from_subroute: '' }))}>
                  {ENTRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Entry Radius (m)</label>
                <input type="number" className={styles.input} value={formData.entry_radius_meters}
                  onChange={e => setFormData(p => ({ ...p, entry_radius_meters: parseInt(e.target.value) || 200 }))} />
              </div>
              <div className={styles.formGroup}>
                <label>Default Direction</label>
                <select className={styles.input} value={formData.default_direction}
                  onChange={e => setFormData(p => ({ ...p, default_direction: e.target.value }))}>
                  <option value="forward">Forward</option>
                  <option value="reverse">Reverse</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.checkLabel}>
                  <input type="checkbox" checked={formData.is_active}
                    onChange={e => setFormData(p => ({ ...p, is_active: e.target.checked }))} />
                  Active
                </label>
              </div>
            </div>

            <div className={styles.formGroup}>
              <div className={styles.labelRow}>
                <label>Location (GPS) *</label>
                {selectedRouteId && (
                  <button type="button" className={styles.pickMapBtn} onClick={() => { setPickingLocation(true); setShowForm(false); }}>
                    <IoLocationOutline size={13} /> Pick from map
                  </button>
                )}
              </div>
              <div className={styles.coordRow}>
                <input required type="number" step="any" className={styles.input} placeholder="Latitude"
                  value={formData.location_lat} onChange={e => setFormData(p => ({ ...p, location_lat: e.target.value }))} />
                <input required type="number" step="any" className={styles.input} placeholder="Longitude"
                  value={formData.location_lng} onChange={e => setFormData(p => ({ ...p, location_lng: e.target.value }))} />
              </div>
              {formData.location_lat && formData.location_lng && (
                <div className={styles.coordPreview}>
                  <IoLocationOutline size={12} /> {parseFloat(formData.location_lat).toFixed(5)}, {parseFloat(formData.location_lng).toFixed(5)}
                </div>
              )}
            </div>

            {formData.entry_type === 'primary' && (
              <div className={styles.formGroup}>
                <label>From Exit Corridor</label>
                <select className={styles.input} value={formData.from_exit_corridor || ''}
                  onChange={e => setFormData(p => ({ ...p, from_exit_corridor: e.target.value || null }))}>
                  <option value="">— None —</option>
                  {corridors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {formData.entry_type === 'from_route' && (
              <div className={styles.formGroup}>
                <label>From Route (intersecting)</label>
                <select className={styles.input} value={formData.from_route || ''}
                  onChange={e => setFormData(p => ({ ...p, from_route: e.target.value || null }))}>
                  <option value="">— Select route —</option>
                  {otherRoutes.map(r => <option key={r.id} value={r.id}>{r.code} — {r.name}</option>)}
                </select>
              </div>
            )}

            {formData.entry_type === 'from_branch' && (
              <div className={styles.formGroup}>
                <label>From Branch</label>
                <select className={styles.input} value={formData.from_subroute || ''}
                  onChange={e => setFormData(p => ({ ...p, from_subroute: e.target.value || null }))}>
                  <option value="">— Select branch —</option>
                  {subroutes.map(s => <option key={s.id} value={s.id}>{s.code}{s.name ? ` — ${s.name}` : ''}</option>)}
                </select>
              </div>
            )}

            <div className={styles.formGroup}>
              <label>Nearest Waypoint <span className={styles.hint}>(first stop from this entry)</span></label>
              <select className={styles.input} value={formData.nearest_waypoint_id || ''}
                onChange={e => setFormData(p => ({ ...p, nearest_waypoint_id: e.target.value || null }))}>
                <option value="">— None —</option>
                {[...routeWaypoints].sort((a, b) => a.sequence_order - b.sequence_order).map(wp => (
                  <option key={wp.id} value={wp.id}>{wp.sequence_order}. {wp.salespoint?.name || wp.salespoint_id}</option>
                ))}
              </select>
            </div>

            <div className={styles.modalFooter}>
              <button type="button" className={styles.cancelBtnSm} onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className={styles.saveBtn} disabled={loading}>
                {editingEP ? 'Save Changes' : 'Create Entry Point'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ── RouteNetworkPage ──────────────────────────────────────────────────────────

export default function RouteNetworkPage() {
  const [activeSection, setActiveSection] = useState('routes');
  const [routes, setRoutes] = useState([]);
  const [salesPoints, setSalesPoints] = useState([]);
  const [depots, setDepots] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadSharedData = async () => {
      try {
        const tokenPayload = decodeToken();
        const [authProfileRes, fullProfileRes] = await Promise.all([
          usersAPI.getProfile().catch(() => null),
          api.get('/api/profile/').catch(() => null),
        ]);

        const entityIds = getLoggedInUserEntityIds(
          tokenPayload,
          authProfileRes?.data,
          fullProfileRes?.data,
        );
        const preferredEntityId = Array.from(entityIds)[0] || null;

        const [routesRes, depotsRes, salesPointList] = await Promise.all([
          routesAPI.getAll().catch(() => null),
          depotsAPI.getAll().catch(() => null),
          loadAllSalesPoints(preferredEntityId).catch(() => []),
        ]);

        if (cancelled) return;

        setRoutes(routesRes?.data?.results || routesRes?.data || []);
        setDepots(depotsRes?.data?.results || depotsRes?.data || []);
        setSalesPoints(
          entityIds.size > 0
            ? salesPointList.filter(sp => isMappedSalesPoint(sp) && salesPointBelongsToUserEntity(sp, entityIds))
            : salesPointList.filter(isMappedSalesPoint)
        );
      } catch (err) {
        console.error('Failed to load shared data', err);
      }
    };

    loadSharedData();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h2>Route Network</h2>
      </div>
      <div className={styles.sectionTabs}>
        {SECTIONS.map(s => (
          <button
            key={s.key}
            className={`${styles.tab} ${activeSection === s.key ? styles.active : ''}`}
            onClick={() => setActiveSection(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className={styles.content}>
        {activeSection === 'routes' && <RoutesSection salesPoints={salesPoints} routes={routes} />}
        {activeSection === 'branches' && <BranchesSection routes={routes} salesPoints={salesPoints} />}
        {activeSection === 'exit-corridors' && <ExitCorridorsSection depots={depots} routes={routes} salesPoints={salesPoints} />}
        {activeSection === 'entry-points' && <EntryPointsSection routes={routes} salesPoints={salesPoints} />}
      </div>
    </div>
  );
}
